import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  applyNfCost,
  getNfCostPreview,
  type NfCostApplyResponse,
  type NfCostPreview,
  type NfCostPreviewItem,
} from "@/lib/adminSupplierInvoicesApi";

interface Props {
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied?: () => void;
}

const DEST_LABEL: Record<string, string> = {
  MATERIA_PRIMA_IN_NATURA: "Matéria-prima",
  MATERIAL_EMBALAGEM: "Embalagem",
  NAO_MAPEADO: "Não mapeado",
};

function money(v: string | null): string {
  return v == null || v === "" ? "—" : v;
}

/**
 * Integração NF→custos. Mostra a prévia do custo de compra SIMPLIFICADO por item (componentes separados,
 * proposto × vigente, variação), com seleção explícita e ciências (divergência de encargos, NF anterior).
 * A aplicação usa SEMPRE o valor recalculado pelo backend — os valores aqui são só para conferência.
 */
export function SupplierInvoiceCostDialog({ invoiceId, open, onOpenChange, onApplied }: Props) {
  const [preview, setPreview] = useState<NfCostPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmNewer, setConfirmNewer] = useState<Set<string>>(new Set());
  const [ackDivergence, setAckDivergence] = useState(false);
  const [result, setResult] = useState<NfCostApplyResponse | null>(null);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const p = await getNfCostPreview(invoiceId);
      setPreview(p);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao carregar a prévia de custo.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (open && invoiceId) {
      setSelected(new Set());
      setConfirmNewer(new Set());
      setAckDivergence(false);
      setResult(null);
      void load();
    }
  }, [open, invoiceId, load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleNewer = (id: string) => {
    setConfirmNewer((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = async () => {
    if (!preview || selected.size === 0) return;
    setApplying(true);
    try {
      const items = preview.items
        .filter((it) => selected.has(it.invoiceItemId))
        .map((it) => ({
          invoiceItemId: it.invoiceItemId,
          expectedProposedCost: it.proposedCost,
          expectedTargetVersion: it.currentVersion,
          confirmReplaceNewer: confirmNewer.has(it.invoiceItemId),
          expectedDestinationType: it.destinationType,
          expectedProductId: it.productId,
          expectedMaterialId: it.materialId,
          expectedReferenceUnit: it.referenceUnit,
          expectedConversionFactor: it.conversionFactor,
        }));
      const res = await applyNfCost({
        invoiceId: preview.invoiceId,
        acknowledgeChargeDivergence: ackDivergence,
        items,
      });
      setResult(res);
      if (res.applied.length > 0) {
        toast.success(`${res.applied.length} custo(s) aplicado(s).`);
        onApplied?.();
      }
      if (res.rejected.length > 0) {
        toast.warning(`${res.rejected.length} item(ns) não aplicado(s) — veja os motivos.`);
      }
      await load();
      setSelected(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao aplicar custos.");
    } finally {
      setApplying(false);
    }
  };

  const canApply =
    !!preview && selected.size > 0 && !applying && (!preview.chargeDivergence || ackDivergence);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Aplicar custo da NF {preview?.number ? `#${preview.number}` : ""}</DialogTitle>
          <DialogDescription>
            Custo de compra <strong>simplificado</strong> (mercadoria + frete + seguro + outras despesas −
            descontos), <strong>sem ajustes tributários específicos</strong>. O rateio é feito sobre todos os
            itens da NF; a aplicação grava o valor recalculado pelo backend.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-6 text-center text-sm text-muted-foreground">Carregando prévia…</div>}

        {!loading && preview && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Fornecedor: {preview.supplierName ?? preview.supplierDocument ?? "—"} · Emissão:{" "}
              {preview.emittedAt ?? "—"}
            </div>

            {preview.chargeDivergence && (
              <div className="rounded border border-warning/40 bg-warning/10 p-2 text-xs">
                <div className="font-medium text-warning">Divergência de encargos</div>
                <ul className="ml-4 list-disc">
                  {preview.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
                <label className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ackDivergence}
                    onChange={(e) => setAckDivergence(e.target.checked)}
                  />
                  Estou ciente da divergência e quero aplicar mesmo assim.
                </label>
              </div>
            )}

            <div className="max-h-[52vh] overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted">
                  <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-left">
                    <th></th>
                    <th>#</th>
                    <th>Item / alvo</th>
                    <th className="text-right">Mercadoria</th>
                    <th className="text-right">Frete</th>
                    <th className="text-right">Seguro</th>
                    <th className="text-right">Outras</th>
                    <th className="text-right">Desc.</th>
                    <th className="text-right">Líquido</th>
                    <th>Conversão</th>
                    <th className="text-right">Proposto</th>
                    <th className="text-right">Vigente</th>
                    <th className="text-right">Var.</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it: NfCostPreviewItem) => {
                    const selectable = it.eligible;
                    return (
                      <tr key={it.invoiceItemId} className="border-t border-border [&>td]:px-2 [&>td]:py-1 align-top">
                        <td>
                          <input
                            type="checkbox"
                            disabled={!selectable}
                            checked={selected.has(it.invoiceItemId)}
                            onChange={() => toggle(it.invoiceItemId)}
                            aria-label={`Selecionar item ${it.nItem}`}
                          />
                        </td>
                        <td>{it.nItem}</td>
                        <td className="max-w-[200px]">
                          <div className="truncate" title={it.description ?? ""}>{it.description ?? "—"}</div>
                          <div className="text-muted-foreground">
                            {DEST_LABEL[it.destinationType] ?? it.destinationType}
                            {it.targetName ? ` · ${it.targetName}` : ""}
                          </div>
                        </td>
                        <td className="text-right">{money(it.productValue)}</td>
                        <td className="text-right">{money(it.freight)}</td>
                        <td className="text-right">{money(it.insurance)}</td>
                        <td className="text-right">{money(it.other)}</td>
                        <td className="text-right">{money(it.discount)}</td>
                        <td className="text-right font-medium">{money(it.netCost)}</td>
                        <td className="whitespace-nowrap text-muted-foreground">
                          {money(it.qCom)} {it.uCom ?? ""} × {money(it.conversionFactor)}
                          <br />= {money(it.convertedQuantity)} {it.referenceUnit ?? ""}
                        </td>
                        <td className="text-right font-medium">
                          {money(it.proposedCost)}
                          {it.referenceUnit ? <span className="text-muted-foreground">/{it.referenceUnit}</span> : null}
                        </td>
                        <td className="text-right text-muted-foreground">{money(it.currentCost)}</td>
                        <td className="text-right">
                          {it.variationPercent != null ? `${it.variationPercent}%` : "—"}
                        </td>
                        <td className="max-w-[180px]">
                          {it.alreadyApplied && <Badge variant="secondary">Já aplicado</Badge>}
                          {!it.eligible && !it.alreadyApplied && (
                            <span className="text-destructive">{it.blockReason}</span>
                          )}
                          {it.eligible && it.olderThanCurrent && (
                            <label className="flex items-center gap-1 text-warning">
                              <input
                                type="checkbox"
                                checked={confirmNewer.has(it.invoiceItemId)}
                                onChange={() => toggleNewer(it.invoiceItemId)}
                              />
                              NF anterior ao custo vigente — confirmar substituição
                            </label>
                          )}
                          {it.eligible && !it.olderThanCurrent && (
                            <span className="text-primary">Elegível</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {result && (
              <div className="space-y-1 text-xs">
                {result.applied.length > 0 && (
                  <div className="rounded border border-primary/30 bg-primary/5 p-2">
                    <div className="font-medium text-primary">Aplicados</div>
                    {result.applied.map((a) => (
                      <div key={a.invoiceItemId}>
                        #{a.nItem} {a.targetName}: {money(a.previousCost)} → {money(a.appliedCost)}
                        {a.referenceUnit ? `/${a.referenceUnit}` : ""}
                      </div>
                    ))}
                  </div>
                )}
                {result.rejected.length > 0 && (
                  <div className="rounded border border-destructive/30 bg-destructive/5 p-2">
                    <div className="font-medium text-destructive">Não aplicados</div>
                    {result.rejected.map((r) => (
                      <div key={r.invoiceItemId}>
                        #{r.nItem}: {r.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Fechar
          </Button>
          <Button onClick={() => void apply()} disabled={!canApply}>
            Aplicar selecionados ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
